import React, { useState, useRef } from 'react';
import { DollarSign, Clock, ChevronDown } from 'lucide-react';

// PromosScreen (aba Cashback) do cardápio público. Extraído VERBATIM de PublicMenu.js
// (Fase 2 refactor). Componente com estado próprio (renderizado como <PromosScreen .../>),
// recebe estado/handlers do pai via props. Mesmo padrão dos modais extraídos.
const PromosScreen = ({ customerPhone, cashbackData, restaurant, loadCashbackData, setCashbackData }) => {
    const [phoneNumber, setPhoneNumber] = useState(customerPhone);
    const [loadingCashback, setLoadingCashback] = useState(false);
    const [expandedFAQ, setExpandedFAQ] = useState(null);
    const promosPhoneRef = useRef(null);

    // Auto-load cashback when phone is available
    React.useEffect(() => {
      if (customerPhone && !cashbackData && restaurant) {
        loadCashbackData(customerPhone);
      }
    }, [customerPhone, restaurant]); // Removed cashbackData to avoid loop

    const handleCheckCashback = async () => {
      const phone = promosPhoneRef.current?.value || phoneNumber;
      if (!phone || phone.length < 10) {
        alert('Por favor, insira um telefone válido');
        return;
      }

      setLoadingCashback(true);
      await loadCashbackData(phone);
      setLoadingCashback(false);
    };

    const formatDate = (dateStr) => {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      return date.toLocaleDateString('pt-BR');
    };

    const faqItems = [
      {
        question: 'Como funciona o cashback?',
        answer: `A cada compra você acumula ${cashbackData?.settings?.percentage || 5}% do valor em créditos que podem ser usados em compras futuras.`
      },
      {
        question: 'Quando posso usar meu cashback?',
        answer: 'Você pode usar seu cashback em qualquer pedido, no momento do checkout.'
      },
      {
        question: 'O cashback expira?',
        answer: `Sim, o cashback expira após ${cashbackData?.settings?.expiry_days || 30} dias.`
      },
      {
        question: 'Como verifico meu saldo?',
        answer: 'Seu saldo é carregado automaticamente quando você faz pedidos.'
      }
    ];

    return (
      <div 
        className="pt-16 pb-20 h-screen overflow-y-auto bg-gray-50"
        data-testid="promos-screen"
      >
        {/* Cashback Tab */}
        <div className="bg-white border-b">
          <div className="px-4 py-2">
            <button className="text-primary font-medium text-sm border-b-2 border-primary pb-2">
              Cashback
            </button>
          </div>
        </div>

        {/* Phone Input Section - Only show if no phone saved */}
        {!customerPhone && !cashbackData && (
          <div className="p-4 bg-white border-b">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Digite seu telefone para verificar seu cashback
            </label>
            <div className="flex gap-2">
              <input
                ref={promosPhoneRef}
                type="tel"
                defaultValue={phoneNumber}
                onBlur={(e) => setPhoneNumber(e.target.value)}
                placeholder="(00) 00000-0000"
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary"
                data-testid="cashback-phone-input"
              />
              <button
                onClick={handleCheckCashback}
                disabled={loadingCashback}
                className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-hover transition-colors disabled:opacity-50"
                data-testid="check-cashback-button"
              >
                {loadingCashback ? 'Carregando...' : 'Verificar'}
              </button>
            </div>
          </div>
        )}

        {/* Cashback Balance Section */}
        {cashbackData && cashbackData.enabled && (
          <div className="p-4 space-y-4">
            {/* Available Balance */}
            <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-xl p-6 border border-blue-100">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="text-primary" size={20} />
                <span className="text-sm text-gray-600">Saldo disponível</span>
              </div>
              <div className="text-3xl font-bold text-blue-600" data-testid="available-balance">
                R$ {cashbackData.available_balance.toFixed(2)}
              </div>
            </div>

            {/* Expired Balance */}
            {cashbackData.expired_balance > 0 && (
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                <div className="flex items-center gap-2 mb-1">
                  <Clock className="text-gray-500" size={16} />
                  <span className="text-sm text-gray-600">
                    R$ {cashbackData.expired_balance.toFixed(2)} expirado em {formatDate(cashbackData.transactions?.find(t => t.expired)?.expires_at)}
                  </span>
                </div>
              </div>
            )}

            {/* Call to Action */}
            <div className="bg-white rounded-xl p-6 text-center border border-gray-200">
              <p className="text-blue-600 font-medium text-lg">
                Compre e ganhe cashback
              </p>
            </div>

            {/* Rules Section */}
            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-blue-600 font-bold">i</span>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 mb-2">Regra</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    Economize mais a cada compra. Todos os pedidos acumulam créditos que você pode 
                    resgatar na próxima compra.
                  </p>
                </div>
              </div>
            </div>

            {/* FAQ Section */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <button
                onClick={() => setExpandedFAQ(expandedFAQ === 'faq' ? null : 'faq')}
                className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                data-testid="faq-toggle"
              >
                <span className="font-medium text-gray-800">Perguntas frequentes</span>
                <ChevronDown 
                  className={`text-gray-400 transition-transform ${expandedFAQ === 'faq' ? 'rotate-180' : ''}`} 
                  size={20} 
                />
              </button>
              
              {expandedFAQ === 'faq' && (
                <div className="px-6 pb-4 space-y-4 border-t border-gray-100" data-testid="faq-content">
                  {faqItems.map((item, index) => (
                    <div key={index} className="space-y-2">
                      <h4 className="font-medium text-gray-800 text-sm">{item.question}</h4>
                      <p className="text-sm text-gray-600">{item.answer}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Change Phone Button */}
            <button
              onClick={() => {
                setCashbackData(null);
                setPhoneNumber('');
              }}
              className="w-full py-3 text-primary font-medium text-sm"
              data-testid="change-phone-button"
            >
              Verificar outro telefone
            </button>
          </div>
        )}

        {/* Cashback Not Enabled Message */}
        {cashbackData && !cashbackData.enabled && (
          <div className="p-4">
            <div className="bg-yellow-50 rounded-xl p-6 border border-yellow-200 text-center">
              <p className="text-yellow-800">
                O programa de cashback não está ativo no momento.
              </p>
            </div>
          </div>
        )}
      </div>
    );
};

export default PromosScreen;
